import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useActualites } from "@/data/hooks";
import { useLanguage } from "@/i18n/LanguageContext";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import Seo from "@/components/Seo";

/* ------------------------------------------------------------------ */
/*  Actualités NANDA SÁ. Ce qui se passe, factuel, daté, vérifiable.  */
/* ------------------------------------------------------------------ */

export default function Actualites() {
  const ACTUALITES = useActualites();
  const { locale } = useLanguage();
  const l = (fr, en, pt) => (locale === "en" ? en : locale === "pt" ? pt : fr);
  const dateLocale = locale === "en" ? "en-GB" : locale === "pt" ? "pt-PT" : "fr-FR";
  const formatDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString(dateLocale, { day: "numeric", month: "long", year: "numeric" }) : null;

  const [featured, ...rest] = ACTUALITES;

  return (
    <div data-testid="page-actualites" className="bg-white">
      <Seo path="/actualites" />

      {/* HERO */}
      <section className="border-b border-nanda-line">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 pt-36 md:pt-48 pb-20 md:pb-28">
          <FadeUp>
            <div className="label-overline text-nanda-muted mb-8">{l("Actualités", "News", "Actualidades")}</div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="font-sans font-light tracking-tight text-nanda-ink text-5xl sm:text-6xl md:text-7xl leading-[1.02] text-balance max-w-4xl">
              {l("Ce qui se passe chez NANDA SÁ.", "What is happening at NANDA SÁ.", "O que se passa na NANDA SÁ.")}
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-10 max-w-2xl text-nanda-inkSoft text-lg md:text-xl font-light leading-relaxed">
              {l(
                "Activités, opérations, arrivages et projets : les nouvelles de nos univers, au Sénégal et en Guinée-Bissau. Des faits, des dates, rien d'autre.",
                "Activities, operations, shipments and projects: news from our universes, in Senegal and Guinea-Bissau. Facts, dates, nothing else.",
                "Actividades, operações, chegadas e projectos: as novidades dos nossos universos, no Senegal e na Guiné-Bissau. Factos, datas, mais nada."
              )}
            </p>
          </FadeUp>
        </div>
      </section>

      {/* FEATURED */}
      {featured && (
        <section className="border-b border-nanda-line">
          <Link
            to={`/actualites/${featured.slug}`}
            data-testid={`actu-featured-${featured.slug}`}
            className="group block hover:bg-nanda-bgAlt transition-colors duration-500"
          >
            <div className="mx-auto max-w-7xl px-6 sm:px-12 py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16 items-center">
              <FadeUp>
                <div className="overflow-hidden rounded-3xl">
                  <img
                    src={featured.cover}
                    alt={featured.coverAlt || featured.title}
                    loading="eager"
                    className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
                  />
                </div>
              </FadeUp>
              <FadeUp delay={0.12}>
                <div className="label-overline text-nanda-terracotta mb-6">
                  {featured.universLabel} · {featured.type}{featured.date ? ` · ${formatDate(featured.date)}` : ""}
                </div>
                <h2 className="font-sans font-light tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-nanda-ink text-balance">
                  {featured.title}
                </h2>
                <p className="mt-6 max-w-lg text-nanda-inkSoft text-base md:text-lg font-light leading-relaxed">
                  {featured.excerpt}
                </p>
                <span className="mt-10 inline-flex items-center gap-2.5 text-sm font-medium text-nanda-ink link-underline">
                  {l("Lire l'actualité", "Read the news", "Ler a notícia")}
                  <ArrowUpRight size={15} strokeWidth={1.5} />
                </span>
              </FadeUp>
            </div>
          </Link>
        </section>
      )}

      {/* LISTE */}
      {rest.length > 0 && (
        <section className="border-b border-nanda-line">
          <div className="mx-auto max-w-7xl px-6 sm:px-12 py-20 md:py-28">
            <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-16">
              {rest.map((a) => (
                <StaggerItem key={a.slug}>
                  <Link to={`/actualites/${a.slug}`} data-testid={`actu-${a.slug}`} className="group block">
                    <div className="overflow-hidden rounded-3xl mb-6">
                      <img
                        src={a.cover}
                        alt={a.coverAlt || a.title}
                        loading="lazy"
                        className="w-full aspect-[16/10] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
                      />
                    </div>
                    <div className="label-overline text-nanda-muted">
                      {a.universLabel} · {a.type}{a.date ? ` · ${formatDate(a.date)}` : ""}
                    </div>
                    <h3 className="font-sans font-medium tracking-tight text-2xl md:text-3xl text-nanda-ink mt-4 leading-tight group-hover:text-nanda-terracotta transition-colors duration-300">
                      {a.title}
                    </h3>
                    <p className="mt-3 text-nanda-inkSoft text-base font-light leading-relaxed max-w-lg">{a.excerpt}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-nanda-ink group-hover:translate-x-1 transition-transform duration-500">
                      {l("Lire", "Read", "Ler")}
                      <ArrowUpRight size={14} strokeWidth={1.5} />
                    </span>
                  </Link>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>
      )}

      {/* PONT VERS LE CARNET */}
      <section className="bg-nanda-bgAlt">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <FadeUp className="md:col-span-8">
            <div className="label-overline text-nanda-muted mb-6">{l("Le Carnet NANDA", "The NANDA Journal", "O Caderno NANDA")}</div>
            <h2 className="font-sans font-light tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-nanda-ink text-balance max-w-2xl">
              {l(
                "Le terrain, les coulisses, les matières.",
                "The field, the backstage, the materials.",
                "O terreno, os bastidores, as matérias."
              )}
            </h2>
            <p className="mt-6 max-w-xl text-nanda-inkSoft text-base md:text-lg font-light leading-relaxed">
              {l(
                "Les Actualités racontent ce qui se passe. Le Carnet raconte comment cela se passe : les lieux, les gestes, les rencontres et les étapes de nos projets.",
                "The News tells what is happening. The Journal tells how it happens: the places, the gestures, the encounters and the steps of our projects.",
                "As Actualidades contam o que se passa. O Caderno conta como acontece: os lugares, os gestos, os encontros e as etapas dos nossos projectos."
              )}
            </p>
          </FadeUp>
          <FadeUp delay={0.15} className="md:col-span-4 flex md:justify-end">
            <Link
              to="/carnet"
              data-testid="actu-cta-carnet"
              className="inline-flex items-center gap-2.5 bg-nanda-terracotta text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#8F5B3F] transition-colors duration-300"
            >
              {l("Lire le Carnet", "Read the Journal", "Ler o Caderno")}
              <ArrowUpRight size={15} strokeWidth={1.5} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
